// Zod schemas for the mcpgraph-gateway REST shapes (prefix /api/v1).
//
// These mirror the §2 data model in ../../SPEC.md and the endpoint table in
// ../../mcpgraph-gateway/SPEC.md. The server has not published its OpenAPI yet,
// so these are the dashboard's interpretation of the frozen field names. Any
// deviation discovered against the real OpenAPI is a CONTRACT note, not a
// silent edit. Every remote payload is parsed through these so a malformed
// response degrades gracefully instead of crashing the UI.

import { z } from 'zod';

// --- shared scalars ------------------------------------------------------

export const Id = z.string().min(1);
export const Timestamp = z.string(); // ISO-8601; kept loose for tolerance

// An optional string that also tolerates an explicit `null` from the server
// (FastAPI/SQLAlchemy emit `null` for unset columns, not an absent key). We
// normalize null → undefined so downstream `?? fallback` reads keep working and
// the OUTPUT type stays `string | undefined`.
export const OptStr = z
  .string()
  .nullish()
  .transform((v) => v ?? undefined);

// Copy server alias keys onto the dashboard's canonical keys (only when the
// canonical key is absent), then validate. Lets one schema accept BOTH the real
// server field names and the dashboard/mock names without losing the canonical
// OUTPUT shape the views read. `null` aliases are ignored (treated as absent).
function withAliases<T extends z.ZodTypeAny>(aliases: Record<string, string>, schema: T) {
  return z.preprocess((v) => {
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      const o = { ...(v as Record<string, unknown>) };
      for (const [alias, canon] of Object.entries(aliases)) {
        if (o[canon] === undefined && o[alias] !== undefined && o[alias] !== null) o[canon] = o[alias];
      }
      return o;
    }
    return v;
  }, schema);
}

// --- tenancy & identity --------------------------------------------------

export const OrgPlan = z.enum(['free', 'pro', 'enterprise']);
export const Organization = z.object({
  id: Id,
  name: z.string(),
  plan: OrgPlan.catch('free'),
});
export type Organization = z.infer<typeof Organization>;

export const UserRole = z.enum(['admin', 'user']);
export const UserStatus = z.enum(['active', 'invited', 'suspended']);
// The auth user object freezes `name`, but the admin `GET /users` list returns
// `display_name` — accept either (see CONTRACT_DRIFT.md).
export const User = withAliases(
  { display_name: 'name' },
  z.object({
    id: Id,
    org_id: Id,
    email: z.string(),
    name: OptStr,
    role: UserRole.catch('user'),
    status: UserStatus.catch('active'),
    created_at: OptStr,
    // OTP1 — set while an admin-issued one-time password is pending (never the password itself).
    one_time_password_expires_at: OptStr,
    // EV1 — false only for a self-registration still waiting for its confirmation link. Absent
    // (an older gateway) means verified: read it as `email_verified !== false`.
    email_verified: z.boolean().optional(),
    email_verified_at: OptStr,
  }),
);
export type User = z.infer<typeof User>;

// OTP1 — POST /users/{id}/password/reset-one-time (admin). The ONLY response that ever carries the
// temporary password: the gateway stores just its hash, so it cannot be fetched again. Keep it in
// component state while the dialog is open and nowhere else (no store, no log, no URL).
export const OneTimePasswordReset = z.object({
  user_id: Id,
  // EV1 — null when the admin chose "e-mail only": the letter is then the password's only copy.
  temporary_password: z.string().min(1).nullable(),
  expires_at: z.string(),
  password_change_required: z.boolean().default(true),
  sessions_revoked: z.number().default(0),
  tokens_revoked: z.number().default(0),
  // EV1 — the letter with the password was handed to the gateway's mailer.
  emailed: z.boolean().default(false),
});
export type OneTimePasswordReset = z.infer<typeof OneTimePasswordReset>;

// Request body for POST /users (admin create). `password` is optional — an empty
// value provisions an SSO-only user (no local password). `display_name` maps to
// the user's `name`. `role` defaults to 'user' server-side.
export const UserCreate = z.object({
  email: z.string().min(1),
  password: z.string().nullish(),
  role: UserRole.optional(),
  display_name: z.string().nullish(),
  email_verified: z.boolean().optional(),
});
export type UserCreate = z.infer<typeof UserCreate>;

// Request body for PATCH /users/{id} (admin update). All fields optional.
export const UserPatch = z.object({
  role: UserRole.optional(),
  status: UserStatus.optional(),
  display_name: z.string().nullish(),
});
export type UserPatch = z.infer<typeof UserPatch>;

export const SsoProvider = z.enum(['gitlab', 'google']);
export const SsoIdentity = z.object({
  user_id: Id,
  provider: SsoProvider,
  subject: z.string(),
});
export type SsoIdentity = z.infer<typeof SsoIdentity>;

export const MembershipRole = z.enum(['owner', 'admin', 'member']);
export const NetworkMembership = z.object({
  user_id: Id,
  network_id: Id,
  role: MembershipRole.catch('member'),
  // Denormalized for display; optional so the core join still validates.
  user_email: z.string().optional(),
});
export type NetworkMembership = z.infer<typeof NetworkMembership>;

// Request body for POST /networks/{id}/members (owner/admin add a member). `role`
// defaults to 'member' server-side; ∈ owner/admin/member.
export const MemberAdd = z.object({
  user_id: Id,
  role: MembershipRole.optional(),
});
export type MemberAdd = z.infer<typeof MemberAdd>;

// --- network & service definition ---------------------------------------

export const NetworkVisibility = z.enum(['private', 'org', 'public']);
export const NetworkStatus = z.enum(['active', 'deleting']);
// §5B: governs object replication. `local-first` (default) keeps scratch local and
// replicates tool outputs to the global tier on finalize; `always-global` replicates
// every object immediately.
export const DurabilityPolicy = z.enum(['local-first', 'always-global']);
export const Network = z.object({
  id: Id,
  org_id: Id,
  slug: z.string(),
  name: z.string(),
  owner_id: Id,
  // Server emits `null` for an unset description; coerce to '' for display.
  description: z
    .string()
    .nullish()
    .transform((v) => v ?? ''),
  visibility: NetworkVisibility.catch('private'),
  status: NetworkStatus.catch('active'),
  // §2/§5B object-store policy + the network's durable global tier instance.
  durability_policy: DurabilityPolicy.catch('local-first'),
  global_tier_instance: OptStr,
  // Optional rollups for the overview/graph.
  member_count: z.number().int().optional(),
  service_count: z.number().int().optional(),
});
export type Network = z.infer<typeof Network>;

// Request body for POST /networks (create a network). Available to ANY user —
// the caller becomes the network's owner. `slug` + `name` are required; the rest
// default server-side (visibility → private, durability_policy → local-first).
export const NetworkCreate = z.object({
  slug: z.string().min(1),
  name: z.string().min(1),
  description: z.string().nullish(),
  visibility: NetworkVisibility.optional(),
  durability_policy: DurabilityPolicy.optional(),
});
export type NetworkCreate = z.infer<typeof NetworkCreate>;

// Request body for PATCH /networks/{id} (owner/admin edit). All fields optional;
// slug is immutable server-side so it is not editable here.
export const NetworkPatch = z.object({
  name: z.string().min(1).optional(),
  description: z.string().nullish(),
  visibility: NetworkVisibility.optional(),
  durability_policy: DurabilityPolicy.optional(),
});
export type NetworkPatch = z.infer<typeof NetworkPatch>;

// --- streaming channels & task-staging file fields (proto additive) ------

// Multi-channel streaming (§5A): a streaming tool / stream descriptor declares its
// bidirectional channels. DOWN = tool->consumer (generated media), UP = consumer->tool
// (mic/camera/keyboard). Mirrors proto ChannelSpec + ChannelDirection. The wire may
// carry direction as up/down or UP/DOWN; we normalize to upper-case.
//
// NB: an UNKNOWN direction is REJECTED, not silently coerced to DOWN. Coercing
// an unrecognized direction (e.g. a future "BIDI") to DOWN would mislabel an
// UP/control channel as generated media and render it as such — worse than
// dropping it. The channel carrying it is dropped via `ChannelList` below.
export const ChannelDirection = z.preprocess(
  (v) => (typeof v === 'string' ? v.toUpperCase() : v),
  z.enum(['UP', 'DOWN']),
);
export type ChannelDirection = z.infer<typeof ChannelDirection>;

export const Channel = z.object({
  channel_id: z.number().int(),
  name: z.string(),
  direction: ChannelDirection,
  media_type: z.string().optional(),
  codec: z.string().optional(),
});
export type Channel = z.infer<typeof Channel>;

// A channel array that DROPS any element that fails validation (e.g. an unknown
// ChannelDirection) instead of rejecting the whole payload or coercing the bad
// channel. A single malformed channel never poisons the surrounding frame nor
// silently becomes a DOWN channel — the good channels still render.
export const ChannelList = z
  .preprocess(
    (v) => (Array.isArray(v) ? v.filter((c) => Channel.safeParse(c).success) : v),
    z.array(Channel),
  )
  .default([]);

// Task staging (§5B): JSON-pointer arg/result fields carrying object refs, so the
// client transparently stages S3 inputs into a mounted volume and rewrites outputs
// back to s3_keys. Mirrors proto FileField.
export const FileField = z.object({
  pointer: z.string(),
  mount_path: z.string().optional(),
});
export type FileField = z.infer<typeof FileField>;

export const InputStaging = z.enum(['volume', 'ref', 'none']);
export const OutputStaging = z.enum(['volume', 'none']);

export const GpuClass = z
  .object({
    min_vram_gb: z.number().optional(),
    labels: z.array(z.string()).optional(),
  })
  .partial()
  .passthrough();

export const ToolDefinition = z.object({
  id: Id,
  service_definition_id: Id,
  name: z.string(),
  input_schema: z.unknown().optional(), // JSON Schema object
  output_schema: z.unknown().optional(),
  schema_hash: z.string(),
  idempotent: z.boolean().default(false),
  stateful: z.boolean().default(false),
  streaming: z.boolean().default(false),
  stream_kind: OptStr,
  media_type: OptStr,
  est_cost_class: OptStr,
  // Proto-additive task staging + multi-channel streaming (§5A/§5B).
  file_inputs: z.array(FileField).default([]),
  file_outputs: z.array(FileField).default([]),
  input_staging: InputStaging.catch('ref'),
  output_staging: OutputStaging.catch('none'),
  channels: ChannelList,
});
export type ToolDefinition = z.infer<typeof ToolDefinition>;

// A service definition's catalog kind: a SHARED admin-managed template vs a
// PERSONAL user-owned def. Tolerate an unknown value by treating it as personal.
export const ServiceKind = z.enum(['shared', 'personal']);
export type ServiceKind = z.infer<typeof ServiceKind>;

export const ServiceDefinition = z.object({
  id: Id,
  org_id: Id,
  owner_id: Id,
  name: z.string(),
  image_ref: z.string(),
  spec_hash: z.string(),
  // Catalog model fields (added with the service-catalog API). Optional so the
  // older bare /service-definitions shape still validates.
  kind: ServiceKind.catch('personal').optional(),
  active: z.boolean().optional(),
  gpu_class: GpuClass.optional(),
  default_env_schema: z.unknown().optional(),
  created_at: Timestamp.optional(),
  tools: z.array(ToolDefinition).default([]),
});
export type ServiceDefinition = z.infer<typeof ServiceDefinition>;

// Request body for POST /service-definitions (owner create). Tools/schemas are
// registered separately by the providing client; the dashboard creates the
// definition shell (name + image + optional GPU floor). The gateway requires a
// `spec_hash` — the dashboard supplies a deterministic placeholder for a shell.
export const ServiceDefinitionCreate = z.object({
  name: z.string().min(1),
  image_ref: z.string().min(1),
  spec_hash: z.string().min(1),
  gpu_class: GpuClass.optional(),
  default_env_schema: z.unknown().optional(),
});
export type ServiceDefinitionCreate = z.infer<typeof ServiceDefinitionCreate>;

// Request body for PATCH /service-definitions/{id} (owner edit). `active` is the
// admin-only global activate/deactivate toggle of a SHARED template.
export const ServiceDefinitionPatch = z.object({
  name: z.string().min(1).optional(),
  image_ref: z.string().min(1).optional(),
  gpu_class: GpuClass.optional(),
  active: z.boolean().optional(),
});
export type ServiceDefinitionPatch = z.infer<typeof ServiceDefinitionPatch>;

// --- service catalog + per-user activation -------------------------------

// Compact tool descriptor embedded in catalog entries (GET /service-catalog).
export const ServiceToolSummary = z.object({
  id: Id,
  name: z.string(),
  schema_hash: z.string(),
  input_schema: z.unknown().optional(),
  output_schema: z.unknown().optional(),
  streaming: z.boolean().default(false),
  idempotent: z.boolean().default(false),
  media_type: OptStr,
});
export type ServiceToolSummary = z.infer<typeof ServiceToolSummary>;

// One row of GET /service-catalog: a service-def the caller can see + their
// activation state. `active` is the admin global flag (shared only);
// `active_for_me` is whether it is effective for the caller (shared: not
// opted-out & globally active; personal: always true).
export const ServiceCatalogEntry = z.object({
  id: Id,
  name: z.string(),
  // Source-provided entries (config templates / remote sources) carry no DB owner → empty string.
  owner_id: z.string(),
  kind: ServiceKind.catch('personal'),
  active: z.boolean(),
  active_for_me: z.boolean(),
  image_ref: z.string(),
  spec_hash: z.string(),
  gpu_class: GpuClass.optional(),
  default_env_schema: z.unknown().optional(),
  // H13: where this entry came from (config|db|<remote-id>) + its kind, for the source badge.
  source: z.string().default('db'),
  source_kind: z.string().default('db'),
  // H14: discovery metadata.
  description: OptStr,
  tags: z.array(z.string()).default([]),
  created_at: Timestamp.optional(),
  updated_at: Timestamp.optional(),
  tools: z.array(ServiceToolSummary).default([]),
});
export type ServiceCatalogEntry = z.infer<typeof ServiceCatalogEntry>;

// H13: a catalog source the admin can manage (built-in config/db + custom remotes).
export const CatalogSource = z.object({
  id: Id,
  kind: z.string(),
  name: z.string(),
  enabled: z.boolean(),
  priority: z.number(),
  url: OptStr,
  builtin: z.boolean().default(false),
  has_credentials: z.boolean().default(false),
  ok: z.boolean().optional(),
  count: z.number().optional(),
  error: OptStr,
});
export type CatalogSource = z.infer<typeof CatalogSource>;

// H11: a Docker Hub search hit shown as a result card in the import UI.
export const DockerHubSearchResult = z.object({
  repo_name: z.string(),
  namespace: z.string(),
  short_name: z.string(),
  image_ref: z.string(),
  description: z.string().default(''),
  stars: z.number().default(0),
  pulls: z.number().default(0),
  is_official: z.boolean().default(false),
  is_automated: z.boolean().default(false),
  source: z.string().default('dockerhub'),
  source_kind: z.string().default('dockerhub'),
});
export type DockerHubSearchResult = z.infer<typeof DockerHubSearchResult>;

export const DockerHubSearchResponse = z.object({
  query: z.string(),
  results: z.array(DockerHubSearchResult).default([]),
});
export type DockerHubSearchResponse = z.infer<typeof DockerHubSearchResponse>;

// Response of POST /service-definitions/{id}/activation (opt a SHARED def in/out).
export const ServiceActivationOut = z.object({
  service_definition_id: Id,
  kind: ServiceKind.catch('personal'),
  is_active: z.boolean(),
  // H15: networks this service is published (= available) in for the caller after the activation
  // reconcile. The dashboard auto-opens the network slide-out using this as the initial selection.
  published_network_ids: z.array(Id).default([]),
});
export type ServiceActivationOut = z.infer<typeof ServiceActivationOut>;

// --- H15: publish = activation (per-service network availability) ---------
export const PublishNetworkState = z.object({
  network_id: Id,
  slug: z.string(),
  name: z.string(),
  published: z.boolean(),
  can_publish: z.boolean().default(true),
  status: OptStr,
  publication_id: OptStr,
});
export type PublishNetworkState = z.infer<typeof PublishNetworkState>;

export const ServicePublishNetworks = z.object({
  service_definition_id: Id,
  service_slug: z.string(),
  networks: z.array(PublishNetworkState).default([]),
});
export type ServicePublishNetworks = z.infer<typeof ServicePublishNetworks>;

// --- publication ---------------------------------------------------------

export const PublicationStatus = z.enum(['pending', 'approved', 'suspended']);
// Server names the approved schema hash `approved_schema_hash`; accept it as the
// dashboard's `schema_hash`.
export const ServicePublication = withAliases(
  { approved_schema_hash: 'schema_hash' },
  z.object({
    id: Id,
    service_definition_id: Id,
    network_id: Id,
    service_slug: z.string(),
    visibility: NetworkVisibility.catch('org'),
    status: PublicationStatus.catch('pending'),
    exposed_tool_ids: z.array(Id).default([]),
    // Denormalized labels for tables/graph.
    service_name: OptStr,
    network_slug: OptStr,
    schema_hash: OptStr,
  }),
);
export type ServicePublication = z.infer<typeof ServicePublication>;

// --- tool catalog (GET /tools) -------------------------------------------

// Flat, live catalog entry: canonical wire name + structured triple + schemas
// + provider liveness. The server projects ToolDefinition x publication x live
// providers (SPEC §4). Optional fields tolerate a leaner server payload.
// The live server projects the catalog with leaner/different field names than the
// dashboard's canonical shape: `network`/`service`/`tool` (slugs), `description`
// (title), and `live_providers` (provider count). Accept all via aliases so the
// views keep reading the canonical names.
export const CatalogTool = withAliases(
  {
    network: 'network_slug',
    service: 'service_slug',
    tool: 'tool_name',
    description: 'title',
    live_providers: 'providers',
  },
  z.object({
    canonical_name: z.string(), // {network}__{service}__{tool}
    network_slug: OptStr,
    service_slug: OptStr,
    tool_name: OptStr,
    title: OptStr,
    input_schema: z.unknown().optional(),
    output_schema: z.unknown().optional(),
    schema_hash: OptStr,
    idempotent: z.boolean().default(false),
    stateful: z.boolean().default(false),
    streaming: z.boolean().default(false),
    stream_kind: OptStr,
    media_type: OptStr,
    est_cost_class: OptStr,
    // Proto-additive: declared channels + staging fields surface in the catalog so the
    // tester can render multi-channel sessions and show staged file I/O.
    channels: ChannelList,
    file_inputs: z.array(FileField).default([]),
    file_outputs: z.array(FileField).default([]),
    input_staging: InputStaging.optional(),
    output_staging: OutputStaging.optional(),
    providers: z.number().int().default(0), // live provider count
    network_id: Id.optional(),
  }),
);
export type CatalogTool = z.infer<typeof CatalogTool>;

// --- runtime: clients, provider instances, streams -----------------------

export const ResourceStat = z.object({
  cpu_pct: z.number().default(0),
  ram_total_mb: z.number().int().default(0),
  ram_used_mb: z.number().int().default(0),
});
export const GpuStat = z.object({
  index: z.number().int(),
  name: z.string(),
  vram_total_mb: z.number().int().default(0),
  vram_free_mb: z.number().int().default(0),
  util_pct: z.number().int().default(0),
  temp_c: z.number().int().default(0),
});
export type GpuStat = z.infer<typeof GpuStat>;

// Per-GPU stats as carried in the REST `node_profile.gpus[]` snapshot (the
// gateway merges the latest live heartbeat into each client here). `util` is the
// integer utilisation %; `temp_c` the GPU temperature (0/absent on older gateways).
export const Gpu = z.object({
  index: z.number().int(),
  name: z.string().default(''),
  vram_total_mb: z.number().int().default(0),
  vram_free_mb: z.number().int().default(0),
  util: z.number().int().default(0),
  temp_c: z.number().int().default(0),
});
export type Gpu = z.infer<typeof Gpu>;

// ACTUAL gateway `node_profile` shape (GET /clients): scalar `cpu`/`ram` numbers,
// `gpus[]` (enriched from the live heartbeat), and `labels` (e.g. {host}). The
// older nested ResourceStat/`gpu` shape was schema drift that made the Clients
// view error. `.passthrough()` tolerates extra fields (hostname/driver/vram).
export const NodeProfile = z
  .object({
    cpu: z.number().default(0),
    ram: z.number().default(0),
    gpus: z.array(Gpu).default([]),
    labels: z.record(z.string()).default({}),
    hostname: z.string().optional(),
    driver: z.string().optional(),
  })
  .passthrough();
export type NodeProfile = z.infer<typeof NodeProfile>;

export const ClientStatus = z.enum(['online', 'draining', 'offline']);
export const Client = z.object({
  id: Id,
  org_id: Id,
  owner_id: Id,
  name: z.string(),
  status: ClientStatus.catch('offline'),
  last_seen: Timestamp.optional(),
  node_profile: NodeProfile.default({ cpu: 0, ram: 0, gpus: [], labels: {} }),
  // Worker economy + queue visibility.
  credits: z.number().int().optional(),
  queue_depth: z.number().int().optional(),
});
export type Client = z.infer<typeof Client>;

// Response of POST /clients (register a client / console node). Creating a client
// MINTS its first client token — the plaintext `client_token` is shown to the
// user ONCE here (server keeps only a hash), then never returned again. Reuses
// the `Client` schema for the created record.
export const ClientCreated = z.object({
  client: Client,
  client_token: z.string(),
});
export type ClientCreated = z.infer<typeof ClientCreated>;

// --- node-admin desired config (H8) ----------------------------------------
export const NodeAdminService = z.object({
  id: Id,
  name: z.string(),
  kind: z.string(),
  image_ref: z.string(),
  spec_hash: z.string(),
  min_vram_gb: z.number().int().default(0),
  gpu_satisfiable: z.boolean().default(true),
  tool_count: z.number().int().default(0),
  selected: z.boolean().default(false),
  live: z.boolean().default(false),
  tags: z.array(z.string()).default([]),
  created_at: Timestamp.optional(),
  updated_at: Timestamp.optional(),
});
export type NodeAdminService = z.infer<typeof NodeAdminService>;

export const NodeAdminConfig = z.object({
  client_id: Id,
  online: z.boolean().default(false),
  desired_config: z.record(z.unknown()).default({}),
  node_max_vram_mb: z.number().int().default(0),
  services: z.array(NodeAdminService).default([]),
});
export type NodeAdminConfig = z.infer<typeof NodeAdminConfig>;

export const DesiredConfigApply = z.object({
  ok: z.boolean(),
  delivered: z.boolean(),
  desired_config: z.record(z.unknown()).default({}),
  errors: z.array(z.string()).default([]),
});
export type DesiredConfigApply = z.infer<typeof DesiredConfigApply>;

export const ProviderRuntimeState = z.enum(['idle', 'loading', 'busy', 'evicting']);
// ST44: the RICH per-service state the Nodes view renders. The gateway derives it from the live
// registry + the node's container lifecycle; kept as a free string (catch-tolerant) so a newer
// gateway token degrades to the coarse `runtime_state` fallback instead of failing validation.
export const ProviderServiceState = z.enum([
  'live',
  'busy',
  'idle',
  'warming',
  'idle-stopped',
  'failed',
  'crashed',
  'stale',
  'cold',
  'on-disk',
]);
export type ProviderServiceState = z.infer<typeof ProviderServiceState>;
export const ProviderInstance = withAliases(
  { instance_id: 'id' },
  z.object({
  id: Id.optional(),
  client_id: Id,
  service_publication_id: OptStr,
  service_definition_id: OptStr,
  runtime_state: ProviderRuntimeState.catch('idle'),
  // The node-reported container lifecycle token + the gateway-derived rich display `state`.
  // Both optional/loose: an older gateway omits them and the view falls back to `runtime_state`.
  container_state: OptStr,
  state: OptStr,
  capacity: z.number().int().default(0),
  vram_free: z.number().int().optional(),
  last_heartbeat: Timestamp.optional(),
  // The concrete container INSTANCE this provider runs in. A node instantiates a
  // service TEMPLATE (service_definition_id) into one or more containers; each
  // carries a distinct `instance`. For the 1:1 case `instance == service_definition_id`.
  // Additive/optional — older gateways omit it and the row still validates.
  instance: OptStr,
  // Denormalized for graph edges.
  canonical_tools: z.array(z.string()).optional(),
  }),
);
export type ProviderInstance = z.infer<typeof ProviderInstance>;

export const ContainerInfo = z.object({
  service_definition_id: Id,
  // The concrete container INSTANCE id. A service TEMPLATE (service_definition_id)
  // is instantiated into one or more containers; each has a distinct `instance`.
  // For the 1:1 case `instance == service_definition_id`. Additive/optional.
  instance: OptStr,
  container_id: z.string().optional(),
  state: z.string(),
  version: z.string().optional(),
  uptime_s: z.number().int().optional(),
  image_ref: z.string().optional(),
});
export type ContainerInfo = z.infer<typeof ContainerInfo>;

export const StreamState = z.enum(['OPENING', 'ACTIVE', 'CLOSING', 'CLOSED', 'ERRORED']);
// GET /api/v1/streams returns the gateway's `StreamOut` (mirrors StreamHub.info): it names the
// id `stream_id` (+ `canonical_tool`, `seq`), NOT `id` — the old required `id` here made every
// response fail Zod validation ("response schema mismatch for /api/v1/streams"). Accept the
// gateway shape (and a legacy `id`), then expose a unified `id` so the Streams view is unchanged.
export const StreamSession = z
  .object({
    stream_id: Id.optional(),
    id: Id.optional(),
    canonical_tool: z.string().optional(),
    tool_call_id: Id.optional(),
    producer_client_id: Id.optional(),
    stream_class: z.string().optional(),
    media_type: z.string().optional(),
    codec: z.string().optional(),
    status: StreamState.catch('OPENING'),
    consumer_count: z.number().int().default(0),
    bytes: z.number().int().default(0),
    seq: z.number().int().optional(),
    started_at: Timestamp.optional(),
    // §5A multi-channel: the session's declared channels (UP + DOWN). Empty/one-DOWN
    // = the simple producer case.
    channels: ChannelList,
    // WB6 — the SR14 block the hub had always computed and `StreamOut` used to drop. Nullish, not
    // required: a gateway older than WB6 answers without it, and a stream row must then say "not
    // measured" rather than fail the whole list's validation. `RouteHopMetrics` is declared further
    // down the file, so the reference is lazy.
    metrics: z.lazy(() => RouteHopMetrics).nullish(),
    // ADR-H — `drop_oldest` on an UP source, `lossless` on a DOWN producer. The same `drops` count
    // means opposite things under the two, so the policy has to travel beside it.
    backpressure: OptStr,
  })
  .transform((s) => ({ ...s, id: s.id ?? s.stream_id ?? '' }));
export type StreamSession = z.infer<typeof StreamSession>;

// ST41 — a minted consumer/watch token for WS /streams/{id} (the dashboard "Watch" action).
export const StreamWatchToken = z.object({
  stream_id: Id,
  token: z.string(),
  ws_url: z.string().optional(),
  expires_in: z.number().int().optional(),
});
export type StreamWatchToken = z.infer<typeof StreamWatchToken>;

// --- MCP sessions (H49) --------------------------------------------------
// A live /mcp Streamable-HTTP session as tracked by the gateway's in-memory session registry.
export const McpSessionStatus = z.enum(['active', 'idle', 'calling']);
export const McpSession = z.object({
  session_id: Id,
  grant_id: Id,
  user_id: Id,
  principal: z.string(),
  networks: z.array(z.string()).default([]),
  source: z.string().default('oauth'),
  transport: z.string().default('http'),
  started_at: Timestamp,
  last_activity: Timestamp,
  tool_calls: z.number().int().default(0),
  current_tool: z.string().optional(),
  last_method: z.string().optional(),
  status: McpSessionStatus.catch('active'),
});
export type McpSession = z.infer<typeof McpSession>;

// --- tasks & queues ------------------------------------------------------

export const TaskState = z.enum([
  'QUEUED',
  'DISPATCHED',
  'RUNNING',
  'SUCCEEDED',
  'FAILED',
  'REJECTED',
  'CANCELLED',
  'LOST',
]);
export const Usage = z.object({
  gpu_seconds: z.number().default(0),
  bytes: z.number().int().default(0),
  tokens: z.number().int().default(0),
});
export const TaskError = z.object({
  code: z.number().int().optional(),
  message: z.string(),
  retryable: z.boolean().optional(),
});
// Models the live gateway's `TaskOut` (GET /api/v1/tasks, api/schemas.py:TaskOut).
// Field names track the server exactly: `is_async` (not `async`), `progress_fraction`
// (not `fraction`), and `result_ref` is a FREEFORM dict ({ref, model, width, …}) — a
// legacy gateway sent a bare string there, so both are tolerated. `usage` is always
// present on the live endpoint but kept optional so partial/legacy payloads still parse.
export const Task = z.object({
  id: Id,
  canonical_tool: z.string(),
  network_id: Id.optional(),
  priority: z.number().int().default(0),
  state: TaskState.catch('QUEUED'),
  is_async: z.boolean().default(false),
  provider_instance_id: Id.optional(),
  // The input arguments the tool was called with. The gateway may expose them under `arguments`
  // (preferred) or as the small inline `arguments_ref` JSON; both are optional (older gateways omit
  // them) so the task detail renders an Input/arguments block only when present.
  arguments: z.record(z.string(), z.unknown()).optional(),
  arguments_ref: z.union([z.record(z.string(), z.unknown()), z.array(z.unknown()), z.string()]).optional(),
  // FREEFORM: the gateway types result_ref as Any — a dict ({ref,model,…}), a LIST (e.g.
  // project_list returns an array inline), a legacy bare string, or null. Accept them all so a
  // list result never trips "response schema mismatch for /api/v1/tasks".
  result_ref: z.union([z.record(z.string(), z.unknown()), z.array(z.unknown()), z.string()]).optional(),
  // The paged list (GET /tasks) is LIGHT: it leaves result_ref / arguments_ref out and says so
  // here, so "no result" and "result not sent" stay distinguishable. `result_bytes` is an approximate
  // size hint (null/absent when there is no result). All optional — older gateways send none of it.
  result_omitted: z.boolean().optional(),
  arguments_omitted: z.boolean().optional(),
  result_bytes: z.number().optional(),
  output_refs: z.array(z.unknown()).optional(),
  error: TaskError.optional(),
  usage: Usage.optional(),
  progress_fraction: z.number().optional(),
  // ST42 — the four phase timestamps + per-phase durations. `started_at` + `durations_ms`
  // are additive; older gateways omit them and the drawer falls back to the timestamps.
  queued_at: Timestamp.optional(),
  dispatched_at: Timestamp.optional(),
  started_at: Timestamp.optional(),
  finished_at: Timestamp.optional(),
  durations_ms: z
    .object({
      queue: z.number().optional(),
      dispatch: z.number().optional(),
      run: z.number().optional(),
      total: z.number().optional(),
    })
    .partial()
    .optional(),
  // Optional linkage the live /tasks endpoint omits but other shapes may carry.
  requester_grant_id: Id.optional(),
  idempotency_key: z.string().optional(),
  // Optional log tail + owning node link (surfaced in the drawer when the gateway carries them).
  log_tail: z.array(z.string()).optional(),
  client_id: Id.optional(),
});
export type Task = z.infer<typeof Task>;

export const QueueDepth = z.object({
  canonical_tool: z.string(),
  depth: z.number().int().default(0),
  oldest_wait_s: z.number().optional(),
  running: z.number().int().default(0),
  providers: z.number().int().default(0),
});
export type QueueDepth = z.infer<typeof QueueDepth>;

// POST /queues/{tool}:drain → how many QUEUED tasks were cancelled.
export const QueueDrain = z.object({
  canonical_tool: z.string(),
  drained: z.number().int().default(0),
});
export type QueueDrain = z.infer<typeof QueueDrain>;

// POST /tasks:cancel-all → how many in-flight tasks were cancelled.
export const CancelAll = z.object({
  cancelled: z.number().int().default(0),
});
export type CancelAll = z.infer<typeof CancelAll>;

// --- ST40: Prometheus-metrics summary (GET /api/v1/metrics/summary) -------
// A JSON aggregate over the last `window_seconds`, computed from the gateway's
// in-memory metric registries (NOT a scrape of the text /metrics). Tool rows are
// pre-sorted by p95 desc; the dashboard also re-sorts client-side.
export const MetricsToolRow = z.object({
  tool: z.string(),
  count: z.number().int().default(0),
  p50_ms: z.number().default(0),
  p95_ms: z.number().default(0),
  max_ms: z.number().default(0),
  // Optional: per-tool failure count if the gateway supplies it (else 0).
  failures: z.number().int().default(0),
});
export type MetricsToolRow = z.infer<typeof MetricsToolRow>;

export const MetricsSaturation = z.object({
  instance: z.string(),
  value: z.number().default(0),
});
export type MetricsSaturation = z.infer<typeof MetricsSaturation>;

export const MetricsVramHeadroom = z.object({
  instance: z.string(),
  free_mb: z.number().int().default(0),
});
export type MetricsVramHeadroom = z.infer<typeof MetricsVramHeadroom>;

export const MetricsCounters = z
  .object({
    enqueued: z.record(z.number()).default({}),
    dispatched: z.record(z.number()).default({}),
    rate_limited: z.record(z.number()).default({}),
    gc: z
      .object({ candidates: z.number().default(0), reclaimed: z.number().default(0) })
      .partial()
      .default({}),
  })
  .partial()
  .transform((c) => ({
    enqueued: c.enqueued ?? {},
    dispatched: c.dispatched ?? {},
    rate_limited: c.rate_limited ?? {},
    gc: { candidates: c.gc?.candidates ?? 0, reclaimed: c.gc?.reclaimed ?? 0 },
  }))
  .default({});

export const MetricsSummary = z.object({
  window_seconds: z.number().default(300),
  generated_at_ms: z.number().default(0),
  tools: z.array(MetricsToolRow).default([]),
  counters: MetricsCounters,
  saturation: z.array(MetricsSaturation).default([]),
  vram_headroom_mb: z.array(MetricsVramHeadroom).default([]),
});
export type MetricsSummary = z.infer<typeof MetricsSummary>;

// --- AF6: workspace-assistant per-turn metrics (GET {assistant}/api/v1/metrics/summary)
// A JSON aggregate over the last `window_seconds`, computed in-process by the assistant
// service (NOT a scrape of its text /metrics). Every field carries a default so a partial
// payload from an older assistant never crashes the tab.
const CountMap = z.record(z.number()).default({});

// count/p50/p95/max in MILLISECONDS (per-turn durations: ttft / classify / think / turn).
export const AssistantDurStat = z.object({
  count: z.number().int().default(0),
  p50_ms: z.number().default(0),
  p95_ms: z.number().default(0),
  max_ms: z.number().default(0),
});
export type AssistantDurStat = z.infer<typeof AssistantDurStat>;

// count/p50/p95/max/avg over raw numbers (prompt size, tool-loop depth).
export const AssistantNumStat = z.object({
  count: z.number().int().default(0),
  p50: z.number().default(0),
  p95: z.number().default(0),
  max: z.number().default(0),
  avg: z.number().default(0),
});
export type AssistantNumStat = z.infer<typeof AssistantNumStat>;

// MC10 — one row of the per-model turn breakdown: which model a turn ran on + its role.
export const AssistantTurnModelRow = z.object({
  model: z.string().default('unknown'),
  role: z.string().default('frontline'),
  count: z.number().int().default(0),
});
export type AssistantTurnModelRow = z.infer<typeof AssistantTurnModelRow>;

// MC10 — one row of the per-model subchat breakdown (lifecycle counts for a child model).
export const AssistantSubchatModelRow = z.object({
  model: z.string().default('unknown'),
  spawned: z.number().int().default(0),
  done: z.number().int().default(0),
  failed: z.number().int().default(0),
  cancelled: z.number().int().default(0),
});
export type AssistantSubchatModelRow = z.infer<typeof AssistantSubchatModelRow>;

// MC10 — non-blocking subchat lifecycle stats (spawn→terminal counters, duration, concurrency).
export const AssistantSubchats = z
  .object({
    total_spawned: z.number().int().default(0),
    events: CountMap,
    by_model: z.array(AssistantSubchatModelRow).default([]),
    duration_ms: z
      .object({
        count: z.number().int().default(0),
        p50_ms: z.number().default(0),
        p95_ms: z.number().default(0),
        max_ms: z.number().default(0),
      })
      .default({}),
    active: z.number().int().default(0),
    max_active: z.number().int().default(0),
  })
  .default({});
export type AssistantSubchats = z.infer<typeof AssistantSubchats>;

// AE11 — one row of the per-ENGINE turn rollup (the engine A/B table). Only the fields the metrics
// layer tags by engine are present; per-engine subchat / deep-turn-cost are NOT emitted (shown as
// "—" on the dashboard). Every field carries a default so a partial/older payload never crashes.
export const AssistantEngineStatRow = z.object({
  engine: z.string().default('in-proc'),
  turns: z.number().int().default(0),
  outcomes: CountMap,
  durations_ms: z
    .object({
      ttft: AssistantDurStat.default({}),
      turn: AssistantDurStat.default({}),
    })
    .default({}),
  tool_iterations: AssistantNumStat.default({}),
});
export type AssistantEngineStatRow = z.infer<typeof AssistantEngineStatRow>;

// SR19 — the voice / mouth-to-ear stage family (VR12 + the CL7 call marks).
//
// `stages` is a RECORD rather than a fixed object on purpose: the service adds a stage name when it
// learns to time one more row of the budget, and a closed object would make every such addition a
// dashboard release. The stage keys this panel knows about are named in `lib/latencyBudget.ts`; an
// unknown key costs nothing, and a missing one reads as "not measured" rather than as zero.
export const AssistantVoiceSummary = z
  .object({
    stages: z.record(AssistantDurStat).default({}),
    mouth_to_ear_by_asr: z
      .array(
        z.object({
          asr: z.string().default('unknown'),
          count: z.number().int().default(0),
          p50_ms: z.number().default(0),
          p95_ms: z.number().default(0),
          max_ms: z.number().default(0),
        }),
      )
      .default([]),
    // The pre-SR19 single-target block. Kept because older assistant builds send only this one.
    target: z
      .object({
        mouth_to_ear_p50_s: z.number().default(2.5),
        mouth_to_ear_within_target: z.number().int().default(0),
        mouth_to_ear_total: z.number().int().default(0),
      })
      .default({}),
    // SR19 — both budgets side by side: the push-to-talk tract and the streaming call route, each
    // counted over its own capture mode. Optional, so a stand running an older assistant still
    // renders (the panel then falls back to the measured p50 against its own constant).
    targets: z
      .object({
        ptt_p50_s: z.number().default(2.5),
        realtime_p50_s: z.number().default(0.8),
        ptt: z
          .object({ within: z.number().int().default(0), total: z.number().int().default(0) })
          .default({}),
        realtime: z
          .object({ within: z.number().int().default(0), total: z.number().int().default(0) })
          .default({}),
      })
      .optional(),
  })
  .default({});
export type AssistantVoiceSummary = z.infer<typeof AssistantVoiceSummary>;

export const AssistantMetricsSummary = z.object({
  window_seconds: z.number().default(86400),
  generated_at_ms: z.number().default(0),
  turns: z
    .object({
      total: z.number().int().default(0),
      outcomes: CountMap,
      escalation_reasons: CountMap,
      fallbacks: CountMap,
      corrections_applied: z.number().int().default(0),
      // MC10 — per (model, role) turn breakdown, desc by count.
      by_model: z.array(AssistantTurnModelRow).default([]),
    })
    .default({}),
  // AE11 — per-engine A/B rollup (outcomes + TTFT/turn durations + tool-loop depth per engine).
  engines: z.array(AssistantEngineStatRow).default([]),
  // MC10 — non-blocking subchat statistics.
  subchats: AssistantSubchats,
  // SR19 — the per-stage mouth-to-ear family the Latency panel reads.
  voice: AssistantVoiceSummary,
  reviews: z
    .object({
      total: z.number().int().default(0),
      verdicts: CountMap,
    })
    .default({}),
  durations_ms: z
    .object({
      ttft: AssistantDurStat.default({}),
      classify: AssistantDurStat.default({}),
      think: AssistantDurStat.default({}),
      turn: AssistantDurStat.default({}),
    })
    .default({}),
  prompt_chars: AssistantNumStat.default({}),
  prompt_tokens_est: AssistantNumStat.default({}),
  tool_iterations: AssistantNumStat.default({}),
});
export type AssistantMetricsSummary = z.infer<typeof AssistantMetricsSummary>;

// --- MC2/MC3: workspace-assistant model-role config (admin, via /assistant-api/) ---
// One role's resolved config: its current model + whether it came from the env default or an admin
// override, the env default itself, and (subchat) the allow-list. `provider` is `vllm` for a
// text-llm model (a real select) or `openclaw` for the escalation tier (select disabled — it is not
// a vLLM model). Every field carries a default so a partial payload never crashes the tab.
export const AssistantModelRole = z.object({
  role: z.string(),
  description: z.string().default(''),
  provider: z.enum(['vllm', 'openclaw']).catch('vllm'),
  supports_allowlist: z.boolean().default(false),
  default: z.string().nullable().default(null),
  override: z.string().nullable().default(null),
  current: z.string().nullable().default(null),
  source: z.enum(['default', 'override']).catch('default'),
  allowlist: z.array(z.string()).default([]),
  is_default: z.boolean().default(true),
});
export type AssistantModelRole = z.infer<typeof AssistantModelRole>;

export const AssistantModelRoles = z.object({
  roles: z.array(AssistantModelRole).default([]),
});
export type AssistantModelRoles = z.infer<typeof AssistantModelRoles>;

// GET /admin/models — the LIVE text-llm model id list backing the dropdown.
export const AssistantModels = z.object({
  models: z.array(z.string()).default([]),
});
export type AssistantModels = z.infer<typeof AssistantModels>;

// --- AE11: engines (dashboard Engines section, via /assistant-api/) ---------
// An engine's AEP capability descriptor (GET /capabilities shape). All optional caps default false
// so an unreachable engine (conservative fallback) renders every badge off.
export const AssistantEngineCaps = z
  .object({
    version: z.string().default(''),
    engine: z.string().default(''),
    subchats: z.boolean().default(false),
    memory: z.boolean().default(false),
    thinking: z.boolean().default(false),
    models: z.array(z.string()).default([]),
  })
  .default({});
export type AssistantEngineCaps = z.infer<typeof AssistantEngineCaps>;

// GET /admin/engines — one engine: default flag, builtin/remote kind, live health, capabilities.
export const AssistantEngineInfo = z.object({
  name: z.string(),
  is_default: z.boolean().default(false),
  kind: z.enum(['builtin', 'remote']).catch('remote'),
  base_url: z.string().nullable().default(null),
  health: z
    .object({ ok: z.boolean().default(false) })
    .passthrough()
    .default({ ok: false }),
  capabilities: AssistantEngineCaps,
});
export type AssistantEngineInfo = z.infer<typeof AssistantEngineInfo>;

export const AssistantAdminEngines = z.object({
  engines: z.array(AssistantEngineInfo).default([]),
  default: z.string().default('in-proc'),
});
export type AssistantAdminEngines = z.infer<typeof AssistantAdminEngines>;

// --- MEMBER engines (non-admin, via /assistant-api/) ------------------------
// GET /api/v1/engines — the engines a member may pick from (names + which is the effective default),
// plus `selectable` (>1 engine → show the picker). No health/capabilities (those stay admin-only).
export const AssistantMemberEngineRow = z.object({
  name: z.string(),
  is_default: z.boolean().default(false),
});
export type AssistantMemberEngineRow = z.infer<typeof AssistantMemberEngineRow>;

export const AssistantMemberEngines = z.object({
  engines: z.array(AssistantMemberEngineRow).default([]),
  default: z.string().default('in-proc'),
  selectable: z.boolean().default(false),
});
export type AssistantMemberEngines = z.infer<typeof AssistantMemberEngines>;

// GET/PUT /api/v1/engines/default — the caller's per-user default engine. `engine` is the effective
// engine, `source` says whether it is the member's own pick ('user') or falls back to the org
// ('global'); `global_default` is the org default. `engines[].is_default_for_me` flags the member's
// current choice. PUT body `{engine: string|null}` sets/clears it (null → revert to global).
export const AssistantMyDefaultEngine = z.object({
  engine: z.string().default('in-proc'),
  source: z.enum(['user', 'global']).catch('global'),
  global_default: z.string().default('in-proc'),
  selectable: z.boolean().default(false),
  engines: z
    .array(z.object({ name: z.string(), is_default_for_me: z.boolean().default(false) }))
    .default([]),
});
export type AssistantMyDefaultEngine = z.infer<typeof AssistantMyDefaultEngine>;

// --- ST43: Projects (GET /api/v1/projects...) ----------------------------
// The live gateway's ProjectOut names the id `project_id` (not `id`) and carries
// `updated_at`; it does NOT return `slug`/`description`/`member_count` (those come
// from the mock/richer shape). Accept `project_id` as the canonical `id` so the real
// response parses; the display-only extras stay optional and render '—' when absent.
export const Project = withAliases(
  { project_id: 'id' },
  z.object({
    id: Id,
    org_id: Id.optional(),
    name: z.string(),
    slug: OptStr,
    description: OptStr,
    owner_id: Id.optional(),
    network_id: Id.optional(),
    member_count: z.number().int().optional(),
    created_at: Timestamp.optional(),
    updated_at: Timestamp.optional(),
  }),
);
export type Project = z.infer<typeof Project>;

export const ProjectMember = withAliases(
  { display_name: 'name' },
  z.object({
    user_id: Id,
    project_id: Id.optional(),
    role: MembershipRole.catch('member'),
    user_email: z.string().optional(),
    name: OptStr,
  }),
);
export type ProjectMember = z.infer<typeof ProjectMember>;

// --- auth grants & tokens ------------------------------------------------

export const TokenSelectors = z.object({
  allow: z.array(z.string()).default([]),
  deny: z.array(z.string()).default([]),
});
export type TokenSelectors = z.infer<typeof TokenSelectors>;

export const GrantSource = z.enum(['oauth', 'pat']);
// The live gateway's GrantOut names the prefix `label` and (as of the last-used work)
// exposes `last_used_at`; accept both as the dashboard's canonical `token_prefix` /
// `last_used`. NB: an older deployed gateway's GrantOut may omit `last_used_at`
// entirely — the field stays optional and the UI shows "never" in that case.
export const TokenGrant = withAliases(
  { label: 'token_prefix', last_used_at: 'last_used' },
  z.object({
    id: Id,
    user_id: Id,
    source: GrantSource.catch('pat'),
    audience: z.string().optional(),
    scopes: z.array(z.string()).default([]),
    tool_selectors: TokenSelectors.default({ allow: [], deny: [] }),
    network_ids: z.array(Id).default([]),
    expiry: Timestamp.optional(),
    revoked: z.boolean().default(false),
    // Display helpers.
    user_email: z.string().optional(),
    token_prefix: z.string().optional(),
    last_used: Timestamp.optional(),
    created_at: Timestamp.optional(),
  }),
);
export type TokenGrant = z.infer<typeof TokenGrant>;

// UX21 — POST /tokens/purge: how many grant rows were actually DELETED.
export const GrantPurge = z.object({ deleted: z.number().int().default(0) });
export type GrantPurge = z.infer<typeof GrantPurge>;

// The live gateway's ClientTokenOut names the prefix `prefix` (not `token_prefix`)
// and carries `last_used_at`; accept both as the dashboard's canonical
// `token_prefix` / `last_used`.
export const ClientToken = withAliases(
  { prefix: 'token_prefix', last_used_at: 'last_used' },
  z.object({
    id: Id,
    client_id: Id,
    allowed_networks: z.array(z.string()).default([]),
    token_prefix: z.string().optional(),
    last_used: Timestamp.optional(),
    revoked: z.boolean().default(false),
    created_at: Timestamp.optional(),
  }),
);
export type ClientToken = z.infer<typeof ClientToken>;

// Returned ONCE on creation: the plaintext token is shown to the user a single
// time, never stored or returned again (server keeps only a hash + prefix).
// Server returns `{grant_id, token, kind, expires_in}` on mint; map grant_id → id.
export const IssuedToken = withAliases(
  { grant_id: 'id' },
  z.object({
    id: Id,
    token: z.string(),
    token_prefix: OptStr,
  }),
);
export type IssuedToken = z.infer<typeof IssuedToken>;

// --- activity & metering -------------------------------------------------

// The REST audit row uses `created_at`/`actor_user_id`/`target_type`+`target_id`
// (vs the event-bus frame's `ts`/`actor`/`target`). Normalize to the dashboard's
// canonical names; compose `target` from `target_type/target_id` when present.
export const AuditEvent = z.preprocess((v) => {
  if (v && typeof v === 'object' && !Array.isArray(v)) {
    const o = { ...(v as Record<string, unknown>) };
    if (o.ts === undefined && o.created_at != null) o.ts = o.created_at;
    if (o.actor === undefined && o.actor_user_id != null) o.actor = o.actor_user_id;
    if (o.target === undefined) {
      if (o.target_type != null && o.target_id != null) o.target = `${o.target_type}/${o.target_id}`;
      else if (o.target_id != null) o.target = o.target_id;
    }
    return o;
  }
  return v;
}, z.object({
  id: Id,
  ts: Timestamp,
  actor: OptStr,
  action: z.string(),
  target: OptStr,
  detail: z.record(z.unknown()).optional(),
}));
export type AuditEvent = z.infer<typeof AuditEvent>;

// Empty on the live demo; aliases mirror the observed audit drift (created_at/
// status) defensively so the call log tolerates the REST row shape.
export const CallLog = withAliases(
  { created_at: 'ts', status: 'state' },
  z.object({
    id: Id,
    ts: Timestamp,
    canonical_tool: z.string(),
    task_id: Id.optional(),
    user_id: Id.optional(),
    network_id: Id.optional(),
    state: TaskState.catch('QUEUED'),
    duration_ms: z.number().optional(),
    usage: Usage.optional(),
  }),
);
export type CallLog = z.infer<typeof CallLog>;

export const UsageRecord = withAliases(
  // The gateway's GET /usage returns the ledger row id as `id` (UsageOut.id) and the
  // timestamp as `ts`; map both to the canonical names so the row parses (a missing
  // `call_id` was the "response schema mismatch for /api/v1/usage").
  { created_at: 'ts', id: 'call_id' },
  z.object({
    call_id: Id,
    org: Id.optional(),
    user: Id.optional(),
    tool: z.string(),
    gpu_seconds: z.number().default(0),
    bytes: z.number().int().default(0),
    tokens: z.number().int().default(0),
    ts: Timestamp,
  }),
);
export type UsageRecord = z.infer<typeof UsageRecord>;

// GET /usage/by-tool (optional ?project_id=) — a scoped per-tool aggregate, one row
// per canonical tool, ordered by `calls` desc. Scoping is server-side: a normal user
// sees only their own usage (incl. editor task-token-originated calls now attributed
// to the originating user); an admin sees org-wide. The dashboard does no filtering.
export const UsageByTool = z.object({
  tool: z.string(),
  calls: z.number().int().default(0),
  gpu_seconds: z.number().default(0),
  tokens: z.number().int().default(0),
  bytes: z.number().int().default(0),
});
export type UsageByTool = z.infer<typeof UsageByTool>;

// --- auth/session --------------------------------------------------------

export const AuthMe = z.object({
  user: User,
  org: Organization.optional(),
  // Networks the user is a member of, with role (drives admin gating + scope).
  memberships: z.array(NetworkMembership).default([]),
});
export type AuthMe = z.infer<typeof AuthMe>;

// SPEC freezes `{access_token, refresh_token, token_type, user}`; the dashboard's
// bearer fallback reads `token`, so accept `access_token` as that alias (cookie
// auth is still preferred).
export const LoginResponse = withAliases(
  { access_token: 'token' },
  z.object({
    token: z.string().optional(), // bearer for in-memory mode; cookie is preferred
    user: User,
  }),
);
export type LoginResponse = z.infer<typeof LoginResponse>;

// EV1 — POST /auth/register when the gateway requires e-mail confirmation: 202, no session.
export const VerificationPending = z.object({
  verification_required: z.literal(true),
  email: z.string(),
  detail: z.string().optional(),
});
export type VerificationPending = z.infer<typeof VerificationPending>;
export const RegisterResponse = z.union([VerificationPending, LoginResponse]);
export type RegisterResponse = z.infer<typeof RegisterResponse>;

// Public self-registration toggle. GET /auth/registration is unauthenticated and
// tells the login screen whether to surface the "Create an account" link.
export const RegistrationStatus = z.object({
  enabled: z.boolean(),
});
export type RegistrationStatus = z.infer<typeof RegistrationStatus>;

// Public "forgot password" capability. GET /auth/password-reset is unauthenticated and tells the
// login screen whether to surface the "Forgot password?" link (the flow is off unless a notification
// webhook is configured, or it is forced via MCPGRAPH_AUTH_PASSWORD_RESET_ENABLED).
export const PasswordResetStatus = z.object({
  enabled: z.boolean(),
});
export type PasswordResetStatus = z.infer<typeof PasswordResetStatus>;

// AH3 — every sign-in method this deployment REALLY offers (GET /auth/methods, unauthenticated).
// `sso` lists only providers whose client credentials are configured: on a stand without an IdP it
// is empty and NO SSO button is rendered. Previously the buttons were unconditional and each one
// led to a 503 — a control that exists is a promise, and that one was never kept.
export const AuthMethods = z.object({
  password: z.boolean(),
  register: z.boolean(),
  password_reset: z.boolean(),
  sso: z.array(z.string()),
  // EV1 — the gateway can send mail itself; new sign-ups must confirm their address.
  email: z.boolean().default(false),
  email_verification: z.boolean().default(false),
});
export type AuthMethods = z.infer<typeof AuthMethods>;

// AH4 — a one-time "sign in on another device" link (POST /auth/device-link). `url` is the login
// page with the token in its FRAGMENT, so it is a password-equivalent with a fuse: `expires_in` is
// how many seconds it has left, which is why the UI counts down instead of leaving it on screen.
export const DeviceLink = z.object({
  url: z.string(),
  expires_in: z.number(),
  expires_at: z.string().optional(),
});
export type DeviceLink = z.infer<typeof DeviceLink>;

// POST /auth/password-reset/confirm success shape.
export const OkResponse = z.object({ ok: z.boolean() });
export type OkResponse = z.infer<typeof OkResponse>;

// Admin settings (GET/PATCH /settings). Only the public-registration flag for now.
export const Settings = z.object({
  registration_enabled: z.boolean(),
});
export type Settings = z.infer<typeof Settings>;

// --- BF15/BF18 assistant instance config (language + global memory) ---------
// One admin-owned blob the workspace assistant reads before every turn: the default reply
// LANGUAGE (BF15) and the read-only GLOBAL memory layer injected into every conversation (BF18).
export const GlobalMemoryNote = z.object({
  id: z.string(),
  text: z.string(),
  created_at: z.number().nullable().optional(),
});
export type GlobalMemoryNote = z.infer<typeof GlobalMemoryNote>;

export const AssistantConfig = z.object({
  language: z.string(),
  global_memory: z.array(GlobalMemoryNote).default([]),
  // CL17 — the model the assistant's CALL lane speaks with. `.default('')` and not `.optional()`
  // on purpose: an older gateway does not send the key, and "" is exactly what that means here —
  // nobody chose, so the deployment's own default is in effect.
  call_model: z.string().default(''),
});
export type AssistantConfig = z.infer<typeof AssistantConfig>;

/**
 * CL17 — the models the CALL lane may be set to. Read from `text-llm` through the gateway, so the
 * picker cannot go stale the way a hard-coded list would the first time weights land on a node.
 * An empty list is a legitimate answer (the catalogue is unreachable) and the card falls back to a
 * free-text field rather than to a card that cannot be used.
 */
export const AssistantCallModels = z.object({
  models: z.array(z.string()).default([]),
  // CALL-FAST — models the call lane can be set to that are NOT on our cards (a hosted stack).
  // Its own key, not merged into `models`, because the picker has to be able to label it: a local
  // model is warmed and pinned for the call, an external one runs on somebody's account and can hit
  // that account's limits mid-call. `.default([])` keeps an OLDER gateway (which sends no such key)
  // reading exactly as "there are none", which is what it means.
  external: z.array(z.string()).default([]),
  selected: z.string().default(''),
  tool: z.string().default(''),
});
export type AssistantCallModels = z.infer<typeof AssistantCallModels>;

// --- tools tester / mcp call descriptor ----------------------------------

// /mcp tools/call result for a streaming tool returns a stream descriptor
// (see SPEC §5A step 2). For sync/async tools we model a unified call result.
export const StreamDescriptor = z.object({
  kind: z.literal('stream'),
  stream_id: Id,
  ws_url: z.string(),
  media_type: z.string().optional(),
  codec: z.string().optional(),
  // §5A: the bidirectional channel set the consumer should render (DOWN) and may
  // send on (UP). Mirrors the StreamDispatch.channels / proto ChannelSpec list.
  channels: ChannelList,
  schema: z.unknown().optional(),
});
export const TaskHandle = z.object({
  kind: z.literal('task'),
  task_id: Id,
  state: TaskState.catch('QUEUED'),
  poll_url: z.string().optional(),
});
export const SyncResult = z.object({
  kind: z.literal('result'),
  task_id: Id.optional(),
  output: z.unknown(),
  usage: Usage.optional(),
});
export const ToolCallResult = z.discriminatedUnion('kind', [
  StreamDescriptor,
  TaskHandle,
  SyncResult,
]);
export type ToolCallResult = z.infer<typeof ToolCallResult>;
export type StreamDescriptor = z.infer<typeof StreamDescriptor>;

// --- service test panel (H12) --------------------------------------------
// A declarative tester the gateway derives per service (auto from tool schemas +
// the service-shipped runtime_config.test_panel override). The renderer is generic:
// `type` is kept as a plain string so an UNKNOWN control/preview type from a newer
// gateway degrades gracefully (the UI falls back to a JSON input / JSON view) instead
// of failing Zod validation. extra/unknown keys are allowed for the same reason.
export const TestPanelField = z
  .object({
    name: z.string(),
    type: z.string().default('text'),
    label: OptStr,
    description: OptStr,
    required: z.boolean().default(false),
    default: z.unknown().optional(),
    placeholder: OptStr,
    options: z.array(z.unknown()).default([]),
    minimum: z.number().nullish().transform((v) => v ?? undefined),
    maximum: z.number().nullish().transform((v) => v ?? undefined),
    step: z.unknown().optional(),
    multiline: z.boolean().default(false),
    accept: OptStr,
  })
  .passthrough();
export type TestPanelField = z.infer<typeof TestPanelField>;

export const TestPanelResult = z
  .object({
    type: z.string().default('auto'),
    ref_path: OptStr,
    media_type: OptStr,
  })
  .passthrough();
export type TestPanelResult = z.infer<typeof TestPanelResult>;

export const TestPanelTool = z
  .object({
    tool: z.string(),
    title: OptStr,
    description: OptStr,
    canonical_name: OptStr,
    live_providers: z.number().default(0),
    streaming: z.boolean().default(false),
    fields: z.array(TestPanelField).default([]),
    result: TestPanelResult.default({ type: 'auto' }),
  })
  .passthrough();
export type TestPanelTool = z.infer<typeof TestPanelTool>;

export const TestPanel = z.object({
  service_definition_id: z.string(),
  service_name: z.string(),
  version: z.number().default(1),
  title: OptStr,
  source: z.string().default('auto'),
  tools: z.array(TestPanelTool).default([]),
});
export type TestPanel = z.infer<typeof TestPanel>;

// --- distributed object store (§5B) --------------------------------------

// A single replica row from the authoritative location index (SPEC §2
// ObjectLocation): which S3 instance holds a given key. Many rows per key =
// replicas. FROZEN entry shape: {instance, client_id, size, sha256, is_global,
// node_labels}. `instance` is the S3 instance id; `client_id` is the owning
// client (absent for the durable global tier); `node_labels` are the hosting
// node's labels (region/tier/…) used as closeness hints by the resolve path.
export const ObjectLocation = z.object({
  instance: z.string(),
  client_id: Id.optional(),
  size: z.number().int().default(0),
  sha256: z.string().optional(),
  is_global: z.boolean().default(false),
  node_labels: z.record(z.string()).default({}),
});
export type ObjectLocation = z.infer<typeof ObjectLocation>;

// GET /objects/locations?key=<s3_key> — replicas for one key (key is a
// query/body param, never a path segment, because s3 keys contain '/').
export const ObjectLocations = z.object({
  s3_key: z.string(),
  locations: z.array(ObjectLocation).default([]),
});
export type ObjectLocations = z.infer<typeof ObjectLocations>;

// GET /objects?network=&limit=&offset= (admin index browse). FROZEN row shape:
// one row per (key, replica) — {s3_key, network_id, instance, client_id, size,
// sha256, is_global, created_at, last_seen}. `instance` is the S3 instance id
// holding this replica; `client_id` is the owning client (absent for the durable
// global tier). The whole response is the FROZEN envelope `{objects, total}`.
export const ObjectIndexEntry = z.object({
  s3_key: z.string(),
  network_id: Id.optional(),
  instance: z.string().optional(),
  client_id: Id.optional(),
  size: z.number().int().default(0),
  sha256: z.string().optional(),
  is_global: z.boolean().default(false),
  created_at: Timestamp.optional(),
  last_seen: Timestamp.optional(),
});
export type ObjectIndexEntry = z.infer<typeof ObjectIndexEntry>;

// GET /objects envelope — NOT the generic {items,total}: the index browse uses a
// domain-named `objects` array (frozen) alongside the total row count.
export const ObjectIndex = z.object({
  objects: z.array(ObjectIndexEntry).default([]),
  total: z.number().int().optional(),
});
export type ObjectIndex = z.infer<typeof ObjectIndex>;

// POST /objects/upload (multipart {file, network, name?}) → the indexed object's
// authoritative key + size/digest + the resolved network id. The plaintext file is
// streamed to the network's global tier / S3 and indexed; only this envelope returns.
export const ObjectUploadResult = z.object({
  s3_key: z.string(),
  size: z.number().int().default(0),
  sha256: OptStr,
  network_id: Id.optional(),
  is_global: z.boolean().default(false),
});
export type ObjectUploadResult = z.infer<typeof ObjectUploadResult>;

// POST /objects/{key}:public-link — a TTL signed URL minted by the global tier.
export const PublicLink = z.object({
  s3_key: z.string(),
  url: z.string(),
  expires_at: Timestamp.optional(),
  ttl_seconds: z.number().int().optional(),
});
export type PublicLink = z.infer<typeof PublicLink>;

// --- list envelope -------------------------------------------------------

/**
 * Build a schema for a list response. The live server returns a BARE ARRAY for
 * its list endpoints (the SPEC table freezes an envelope only for `/objects`),
 * while the MSW mocks historically used `{items,total}`. Accept BOTH and always
 * expose the canonical `{items,total}` OUTPUT the views read.
 */
export function listOf<T extends z.ZodTypeAny>(item: T) {
  return z.preprocess(
    (v) => (Array.isArray(v) ? { items: v, total: v.length } : v),
    z.object({
      items: z.array(item).default([]),
      total: z.number().int().optional(),
    }),
  );
}

// --- overview aggregate (GET /overview) ----------------------------------

// The server returns a grouped summary `{counts, resources, activity}` (not a
// flat object). All fields default so a leaner payload still validates.
export const Overview = z.object({
  counts: z
    .object({
      networks: z.number().int().default(0),
      clients: z.number().int().default(0),
      clients_online: z.number().int().default(0),
      publications: z.number().int().default(0),
      publications_approved: z.number().int().default(0),
      service_definitions: z.number().int().default(0),
      tools: z.number().int().default(0),
    })
    .partial()
    .transform((c) => ({
      networks: c.networks ?? 0,
      clients: c.clients ?? 0,
      clients_online: c.clients_online ?? 0,
      publications: c.publications ?? 0,
      publications_approved: c.publications_approved ?? 0,
      service_definitions: c.service_definitions ?? 0,
      tools: c.tools ?? 0,
    }))
    .default({}),
  resources: z
    .object({
      live_providers: z.number().int().default(0),
      total_credits: z.number().int().default(0),
      in_flight: z.number().int().default(0),
      vram_free_mb: z.number().int().default(0),
      vram_total_mb: z.number().int().default(0),
    })
    .partial()
    .transform((r) => ({
      live_providers: r.live_providers ?? 0,
      total_credits: r.total_credits ?? 0,
      in_flight: r.in_flight ?? 0,
      vram_free_mb: r.vram_free_mb ?? 0,
      vram_total_mb: r.vram_total_mb ?? 0,
    }))
    .default({}),
  activity: z
    .object({
      queued_tasks: z.number().int().default(0),
      running_tasks: z.number().int().default(0),
      active_streams: z.number().int().default(0),
      queue_depth_by_tool: z.record(z.number()).default({}),
    })
    .partial()
    .transform((a) => ({
      queued_tasks: a.queued_tasks ?? 0,
      running_tasks: a.running_tasks ?? 0,
      active_streams: a.active_streams ?? 0,
      queue_depth_by_tool: a.queue_depth_by_tool ?? {},
    }))
    .default({}),
});
export type Overview = z.infer<typeof Overview>;

// --- routes (network stream pipelines, ADR-020) --------------------------

// A hop: a streaming-tool selector (`service__tool` network-relative, or full
// canonical `network__service__tool`) + optional params. The provider is chosen
// by the scheduler at instantiation — the route fixes only the selector.
export const RouteHop = z.object({
  tool: z.string().min(1),
  params: z.record(z.unknown()).default({}),
  // CL13 — the producer this hop READS: `"source"` (the route's own source stream) or the index of
  // an EARLIER hop. Omitted, it is the linear chain the list has always described: the source for
  // hop 0, the previous hop for the rest. Two hops naming the same producer is a BRANCH — one
  // microphone feeding both the STT chain and a VAD sidecar — which a linear list could not say.
  from: z.union([z.number().int(), z.string()]).nullish(),
});
export type RouteHop = z.infer<typeof RouteHop>;

// CL13 — the route's DOWN end (what the caller listens to). `null`/absent = the last hop, which is
// what it always was. It exists because a branching route has more than one TERMINAL hop, and
// appending a sidecar must not move the end of the call onto the sidecar.
const OutputHop = z.number().int().nullish();

// A declared source/sink end: loop-back to the spawning service, a browser
// UP/DOWN WS, or a tee-write to an object `ref` (fan-out = several sinks).
export const RouteEndKind = z.enum(['loopback', 'browser', 'ref', 'fanout']);
export const RouteEnd = z.object({
  kind: RouteEndKind.catch('loopback'),
  ref: OptStr,
  params: z.record(z.unknown()).default({}),
});
export type RouteEnd = z.infer<typeof RouteEnd>;

export const Route = z.object({
  id: Id,
  network_id: Id,
  name: z.string(),
  version: z.number().int().default(1),
  is_latest: z.boolean().default(true),
  description: OptStr,
  hops: z.array(RouteHop).default([]),
  sources: z.array(RouteEnd).default([]),
  sinks: z.array(RouteEnd).default([]),
  output_hop: OutputHop,
  created_by: OptStr,
  created_at: Timestamp.optional(),
});
export type Route = z.infer<typeof Route>;

// Request body for POST /networks/{id}/routes (create v1).
export const RouteCreate = z.object({
  name: z.string().min(1),
  description: z.string().nullish(),
  hops: z.array(RouteHop).default([]),
  sources: z.array(RouteEnd).default([]),
  sinks: z.array(RouteEnd).default([]),
  output_hop: OutputHop,
});
export type RouteCreate = z.infer<typeof RouteCreate>;

// Request body for PUT /networks/{id}/routes/{name} (edit → a new version).
export const RouteUpdate = z.object({
  description: z.string().nullish(),
  hops: z.array(RouteHop).default([]),
  sources: z.array(RouteEnd).default([]),
  sinks: z.array(RouteEnd).default([]),
  output_hop: OutputHop,
});
export type RouteUpdate = z.infer<typeof RouteUpdate>;

// SR14 — what one hop of a live route is actually doing. Until this existed the Instances table
// rendered `hops.length` under the heading "throughput"; the file said so itself. These are the
// four questions an operator asks of a chain: is it moving, how late is it, is it losing anything,
// and how does it get there.
export const RouteHopMetrics = z.object({
  frames_in: z.number().default(0),
  frames_out: z.number().default(0),
  bytes_in: z.number().default(0),
  bytes_out: z.number().default(0),
  drops: z.number().default(0),
  frames_per_second: z.number().default(0),
  bytes_per_second: z.number().default(0),
  latency_p50_ms: z.number().default(0),
  latency_p95_ms: z.number().default(0),
  latency_samples: z.number().default(0),
  delivery_path: z.string().default('WS'),
  // SR21 — seconds since this link last carried a frame; null if it never carried one. A rate of
  // zero cannot tell those apart, and the difference is the whole idle watchdog.
  idle_seconds: z.number().nullish(),
});
export type RouteHopMetrics = z.infer<typeof RouteHopMetrics>;

// A live route instance (in-memory StreamRouter truth) for the observation view.
export const RouteInstanceHop = z.object({
  index: z.number().int(),
  canonical_tool: z.string(),
  provider_instance_id: z.string(),
  input_stream_id: OptStr,
  output_stream_id: z.string(),
  // Nullish, not required: a gateway older than SR14 answers without it, and an instance that has
  // already been torn down has no live stream left to read.
  metrics: RouteHopMetrics.nullish(),
  // SR21 — `live` | `degraded`. The state a silent call had no word for: this hop is being fed and
  // is producing nothing. Defaulted, so a gateway that predates the watchdog reads as healthy
  // rather than as unknown.
  status: z.string().default('live'),
  degraded_after_s: z.number().nullish(),
  // CL13 — a TERMINAL BRANCH: this hop's output is neither the route output nor read by another
  // hop. The VAD sidecar of a call is the case; its silence is its NORMAL state, which is why the
  // graph must say "tap" next to a hop before anyone reads a degraded badge on it.
  is_tap: z.boolean().default(false),
});

// CL13 — a terminal branch left OUT of a live instance because no provider was live for it. The
// route runs; the sidecar is simply absent. Without a name for the absence, "the VAD said nothing"
// and "there is no VAD" look identical from the browser.
export const RouteSkippedHop = z.object({
  hop: z.number().int(),
  tool: z.string().default(''),
  reason: z.string().default(''),
});
export type RouteSkippedHop = z.infer<typeof RouteSkippedHop>;

export const RouteInstance = z.object({
  instance_id: z.string(),
  route_id: Id,
  route_name: z.string(),
  network_id: Id,
  version: z.number().int().default(1),
  status: z.string(),
  source_stream_id: OptStr,
  output_stream_id: OptStr,
  hops: z.array(RouteInstanceHop).default([]),
  skipped_hops: z.array(RouteSkippedHop).default([]),
  metrics: RouteHopMetrics.nullish(),
  started_ms: z.number().nullish(),
  ended_ms: z.number().nullish(),
});
export type RouteInstance = z.infer<typeof RouteInstance>;

// Route usage (H7) — a route projected for the graph routes layer: its canonical hop chain
// (source → target), the directed edges between consecutive hops, and cumulative usage counters.
export const RouteUsageCounters = z.object({
  instantiations: z.number().int().default(0),
  completions: z.number().int().default(0),
  errors: z.number().int().default(0),
  active: z.number().int().default(0),
  last_used_ms: z.number().nullable().optional(),
});
export type RouteUsageCounters = z.infer<typeof RouteUsageCounters>;
export const RouteUsageEdge = z.object({
  from_tool: z.string(),
  to_tool: z.string(),
});
export type RouteUsageEdge = z.infer<typeof RouteUsageEdge>;
export const RouteUsage = z.object({
  route_id: Id,
  route_name: z.string(),
  network_id: Id,
  version: z.number().int().default(1),
  hops: z.array(z.string()).default([]),
  edges: z.array(RouteUsageEdge).default([]),
  usage: RouteUsageCounters.default({}),
});
export type RouteUsage = z.infer<typeof RouteUsage>;
export const RouteExercise = z.object({
  route_name: z.string(),
  version: z.number().int().default(1),
  outcome: z.string(),
  instance_id: OptStr,
  error: OptStr,
  usage: RouteUsageCounters.default({}),
});
export type RouteExercise = z.infer<typeof RouteExercise>;
